import { memo, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  MenuItem,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import MDBox from "components/MDBox";
import { createProject, updateProject } from "app/services/project.service";

const PROJECT_STATUSES = ["NOT_STARTED", "ON_TRACK", "AT_RISK", "CRITICAL", "ON_HOLD", "COMPLETED"];

const initialData = {
  id: null,
  projectCode: "",
  projectName: "",
  description: "",
  clientId: "",
  clientName: "",
  projectManager: "",
  startDate: "",
  endDate: "",
  budget: "",
  status: "NOT_STARTED",
  progress: 0,
};

const AddProjectDialog = ({ open, setOpen, onSuccess, editObject, clients = [] }) => {
  const [data, setData] = useState(initialData);
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editObject) {
      setData({
        ...editObject,
        startDate: editObject.startDate?.substring(0, 10) || "",
        endDate: editObject.endDate?.substring(0, 10) || "",
      });
    } else {
      setData(initialData);
    }
    setMessageOpen(false);
  }, [editObject, open]);

  const validateForm = () => {
    if (!data.projectCode || data.projectCode.trim() === "") {
      setError("Project code is required");
      return false;
    }
    if (!data.projectName || data.projectName.trim() === "") {
      setError("Project name is required");
      return false;
    }
    return true;
  };

  const setError = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccess = (successMessage) => {
    setMessage(successMessage);
    setMessageOpen(true);
    setSeverity("success");
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      if (editObject) {
        await updateProject(data.id, data);
        setSuccess("Project updated successfully");
      } else {
        await createProject(data);
        setSuccess("Project created successfully");
      }
      setTimeout(() => {
        setData(initialData);
        setMessageOpen(false);
        setOpen(false);
        onSuccess && onSuccess();
      }, 1000);
    } catch (error) {
      setError(error.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setData(initialData);
    setMessageOpen(false);
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editObject ? "Edit Project" : "Create New Project"}</DialogTitle>
      <DialogContent>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="projectCode"
            label="Project Code"
            fullWidth
            variant="outlined"
            value={data.projectCode}
            onChange={(e) => setData({ ...data, projectCode: e.target.value })}
            required
          />
          <TextField
            id="status"
            label="Status"
            fullWidth
            select
            variant="outlined"
            value={data.status}
            onChange={(e) => setData({ ...data, status: e.target.value })}
          >
            {PROJECT_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s.replace("_", " ")}</MenuItem>
            ))}
          </TextField>
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="projectName"
            label="Project Name"
            fullWidth
            variant="outlined"
            value={data.projectName}
            onChange={(e) => setData({ ...data, projectName: e.target.value })}
            required
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="clientId"
            label="Client"
            fullWidth
            select
            variant="outlined"
            value={data.clientId}
            onChange={(e) => {
              const client = clients.find((c) => c.id === e.target.value);
              setData({ ...data, clientId: e.target.value, clientName: client?.clientName || "" });
            }}
          >
            {clients.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.clientName}</MenuItem>
            ))}
          </TextField>
          <TextField
            id="projectManager"
            label="Project Manager"
            fullWidth
            variant="outlined"
            value={data.projectManager}
            onChange={(e) => setData({ ...data, projectManager: e.target.value })}
          />
        </MDBox>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="startDate"
            label="Start Date"
            type="date"
            fullWidth
            variant="outlined"
            value={data.startDate}
            onChange={(e) => setData({ ...data, startDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            id="endDate"
            label="End Date"
            type="date"
            fullWidth
            variant="outlined"
            value={data.endDate}
            onChange={(e) => setData({ ...data, endDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </MDBox>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="budget"
            label="Budget"
            type="number"
            fullWidth
            variant="outlined"
            value={data.budget}
            onChange={(e) => setData({ ...data, budget: parseFloat(e.target.value) || "" })}
          />
          <TextField
            id="progress"
            label="Progress (%)"
            type="number"
            fullWidth
            variant="outlined"
            value={data.progress}
            onChange={(e) => setData({ ...data, progress: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) })}
            inputProps={{ min: 0, max: 100 }}
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="description"
            label="Description"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={data.description}
            onChange={(e) => setData({ ...data, description: e.target.value })}
          />
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            severity={severity}
            action={
              <IconButton size="small" onClick={() => setMessageOpen(false)}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={isSubmitting} variant="contained">
          {editObject ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddProjectDialog);

